/**
 * 可选支付方式与 Stripe 同步契约 schema —— dev-docs `02-API契约/A8-运营支付.md` §7.8.8 / §7.8.9
 * （ADM-PAY-S013 Stripe 支付方式同步）。
 *
 * 可选支付方式 = `system_payment_methods` 中 ACTIVE 且平台适用的目录行 ∪ 该 App 全部 Stripe 同步行
 * （`03-数据字典/70-产品配置.md` §3 / §3.0 / §3.7.5）。
 * 后台的策略成员候选与图标配置入口**只取 §7.8.8 列出的取值**，不提供手输。
 */

import { z } from 'zod';

/** GET /api/v1/payment-method-options query（§7.8.8）：`app_id` 必填，缺失 / 非法 → VALIDATION_FAILED。 */
export const ListPaymentMethodOptionsQuerySchema = z.object({
  app_id: z.string().uuid(),
});
export type ListPaymentMethodOptionsQuery = z.infer<typeof ListPaymentMethodOptionsQuerySchema>;

/** POST /api/v1/payment-method-options/stripe-sync 请求体（§7.8.9，strict：闭集一键）。 */
export const SyncStripePaymentMethodsRequestSchema = z
  .object({
    app_id: z.string().uuid(),
  })
  .strict();
export type SyncStripePaymentMethodsRequest = z.infer<typeof SyncStripePaymentMethodsRequestSchema>;

/** 可选项来源：`FIXED` = 固定子集；`STRIPE_SYNC` = Stripe 同步子集（§7.8.8 字段表）。 */
export const PAYMENT_METHOD_OPTION_SOURCES = ['FIXED', 'STRIPE_SYNC'] as const;
export type PaymentMethodOptionSource = (typeof PAYMENT_METHOD_OPTION_SOURCES)[number];

/** 目录行后台面板分组（`system_payment_methods.admin_group`，70 §3.0）。 */
export const PAYMENT_METHOD_OPTION_GROUPS = ['WALLET', 'CARD', 'OTHER'] as const;
export type PaymentMethodOptionGroup = (typeof PAYMENT_METHOD_OPTION_GROUPS)[number];

/**
 * 单个可选项（§7.8.8 `payment_methods[]`）。
 * - `stripe_type`：同步行的 Stripe 类型键；`FIXED` 为 null。
 * - `is_available`：同步行是否**同步可用**（`is_available=true` 且来源 Account = 顶层 `stripe_account_id`）；
 *   `FIXED` 为 null（固定子集不受同步可用性约束）。`false` 的同步取值仍可配图标、进策略，但当前不下发 C 端。
 */
export type PaymentMethodOption = {
  payment_method: string;
  source: PaymentMethodOptionSource;
  stripe_type: string | null;
  /** 结算通道（`channel_of`）：`FIXED` = 目录行 `channel`；`STRIPE_SYNC` 恒 `STRIPE` */
  channel: string;
  /** `FIXED` = 目录行 `admin_label`；`STRIPE_SYNC` 为 null（后台按 `stripe_type` 展示） */
  label: string | null;
  /** `FIXED` = 目录行 `admin_group`；`STRIPE_SYNC` 为 null */
  group: PaymentMethodOptionGroup | null;
  /**
   * 面板品牌键：`FIXED` = 目录行 `method_key`（NULL = 无品牌，如 `IAP` / 整个供应商收银台）；`STRIPE_SYNC` 为 null，
   * 品牌取 `stripe_type`。后台据此按品牌分组展示、执行「切换收单方」（70 §3.7.2）。
   */
  method_key: string | null;
  /** `true` = 卡品牌固定图标（目录行 `fixed_icon_asset_id` 非空），后台不提供图标配置；其余 `false` */
  icon_fixed: boolean;
  is_available: boolean | null;
};

/**
 * §7.8.8 / §7.8.9 响应 `data`。
 * - `stripe_account_id`：当前未软删 Stripe Checkout 配置的 Account；无配置为 null。
 * - `synced_at`：该 App 同步行的最大 `synced_at`；无同步行为 null。
 * - `payment_methods`：先 ACTIVE 且平台适用的目录行（按 `catalog_order` 升序；WEB 不含 IAP），再同步行（按
 *   `payment_method` 升序）。已退役取值不在列表中，后台对不在列表里的存量成员 / 设置行标示「已退役 / 不可选」。
 */
export type PaymentMethodOptionsResponse = {
  app_id: string;
  stripe_account_id: string | null;
  synced_at: string | null;
  payment_methods: PaymentMethodOption[];
};
